export default class LogoSlider {
    static readonly SPEED_PX_PER_SECOND = 60;

    static init(): void {
        const wraps: NodeListOf<HTMLElement> = document.querySelectorAll('[data-logo-slider]');

        wraps.forEach((wrap) => this.sliderInit(wrap));
    }

    static sliderInit(wrap: HTMLElement): void {
        const track: HTMLElement | null = wrap.querySelector('[data-logo-slider-track]');

        if (!track) {
            return;
        }

        const originalItems: HTMLElement[] = Array.from(track.children) as HTMLElement[];

        if (originalItems.length === 0) {
            return;
        }

        const images: HTMLImageElement[] = originalItems.flatMap((item) => Array.from(item.querySelectorAll('img')));

        const waitForImages = (): Promise<void> =>
            Promise.all(
                images.map((img) =>
                    img.complete
                        ? Promise.resolve()
                        : new Promise<void>((resolve) => {
                              img.addEventListener('load', () => resolve(), { once: true });
                              img.addEventListener('error', () => resolve(), { once: true });
                          }),
                ),
            ).then(() => undefined);

        const build = (): void => {
            track.style.animation = 'none';
            track.innerHTML = '';
            originalItems.forEach((item) => track.appendChild(item.cloneNode(true)));

            // duplicate the item set until the track covers the viewport twice over,
            // so the seamless "shift by one set width" loop never runs out of content
            let period = this.measurePeriod(track, originalItems.length);

            while (period === null || track.scrollWidth < wrap.clientWidth + period) {
                originalItems.forEach((item) => track.appendChild(item.cloneNode(true)));
                period = this.measurePeriod(track, originalItems.length);
            }

            track.style.setProperty('--logo-slider-distance', `${period}px`);
            track.style.setProperty('--logo-slider-duration', `${period / this.SPEED_PX_PER_SECOND}s`);
            track.style.animation = '';
            track.classList.add('is-ready');
        };

        // build only once every logo image has its real intrinsic size - measuring
        // while an <img> is still loading (width 0) produces a wrong loop distance,
        // which then shows up as a visible seam once the images pop in
        waitForImages().then(() => {
            build();

            let lastWidth = wrap.clientWidth;
            let resizeTimer: number | undefined;

            window.addEventListener('resize', () => {
                window.clearTimeout(resizeTimer);
                resizeTimer = window.setTimeout(() => {
                    // mobile browsers fire resize on scroll (address bar show/hide) even
                    // though the width is unchanged - rebuilding then would reset the
                    // animation mid-scroll, so only rebuild on an actual width change
                    if (wrap.clientWidth === lastWidth) {
                        return;
                    }

                    lastWidth = wrap.clientWidth;
                    build();
                }, 200);
            });
        });
    }

    static measurePeriod(track: HTMLElement, setLength: number): number | null {
        const first = track.children[0] as HTMLElement | undefined;
        const firstOfNextSet = track.children[setLength] as HTMLElement | undefined;

        if (!first || !firstOfNextSet) {
            return null;
        }

        return firstOfNextSet.offsetLeft - first.offsetLeft;
    }
}
